import { DeleteOutlined, DownOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Exam } from 'model/response';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Button, Dropdown, Input, MenuProps, message, Modal, Popconfirm, Select, Table } from 'antd';
import {
  useMasterSchoolYearsSelector,
  useMasterSubjectsSelector,
  useMasterUserSelector,
} from '../../../redux/selector';
import { ColumnsType } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store/store';
import { updateLoading } from '../../../redux/slice/appConfigSlice';
import { initExamEdit } from '../../../redux/slice/examEditSlice';
import { approvalStatusList } from 'utils/enumMapper';
import processError from '../../../utils/processError';
import SelectExamTemplate from './SelectExamTemplate';
import {
  deleteExam,
  getGenerateRandomExam,
  hasDeleteExamConfirm,
  pageExams,
  updateApprovalStatus,
} from 'service/examService';
import ExamPreviewModal from './ExamPreviewModal';
import { findExamTemplatesById } from '../../../service/examTemplateService';
import { deletePractice, hasDeletePracticeConfirm, pagePractices } from '../../../service/practiceService';
import EditQuestionPopup from '../../common/EditQuestionPopup';
import useTableConfig from '../../../hooks/useTableConfig';
import { canMasterCompile } from '../../../utils/utils';
import useApiDelay from '../../../hooks/useApiDelay';

type ModalDataType = {
  open: boolean;
  exam?: Exam;
};

interface ExamTableManagementProps {
  type?: 'exam' | 'practice';
}

export default function ExamTableManagement({ type = 'exam' }: ExamTableManagementProps) {
  const params = useParams();
  const navigate = useNavigate();
  const schoolYearList = useMasterSchoolYearsSelector();
  const subjects = useMasterSubjectsSelector();
  const [examSubjectPage, setExamSubjectPage] = useState<{}>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [schoolYearFilter, setSchoolYearFilter] = useState<string>('');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<string>('');
  const [preview, setPreview] = useState<ModalDataType>({
    open: false,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{
    examId?: null | string;
    open: boolean;
  }>({
    open: false,
  });
  const user = useMasterUserSelector();
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig([keyword, schoolYearFilter, approvalStatusFilter]);

  const rootElement: any = useRef(null);
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Exam[]>();
  const [loading, setLoading] = useState(false);

  const items: MenuProps['items'] = [
    {
      label: 'Sinh đề ngẫu nhiên',
      key: 'generate-random-exams',
    },
  ];

  function handlePreview(exam: Exam) {
    setPreview({
      open: true,
      exam: exam,
    });
  }

  const columns: ColumnsType<Exam> = [
    {
      title: 'Tên đề',
      dataIndex: 'name',
      width: '45%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Năm',
      dataIndex: 'schoolYear',
      render: (_, record) => (record.schoolYear ? record.schoolYear.code : ''),
      width: '15%',
      align: 'center',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'approvalStatus',
      render: (approvalStatus, record) => {
        return (
          <Select
            onChange={(e) => onUpdateApprovalStatusRecord(e, record)}
            className="w-full"
            placeholder="Trạng thái"
            style={{ flex: 1 }}
            value={approvalStatus}
            options={approvalStatusList}
            disabled={!canMasterCompile(user?.role)}
          />
        );
      },
      width: '15%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('approvalStatus'),
    },
    {
      title: 'Xem trước',
      key: 'preview',
      width: '15%',
      align: 'center',
      render: (_, exam) => (
        <div className={'flex justify-center p-2 cursor-pointer'} onClick={() => handlePreview(exam)}>
          <EyeOutlined className={'text-primary text-[18px]'} />
        </div>
      ),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      width: '10%',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => moveEditView(record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title={type === 'exam' ? 'Xóa đề thi' : 'Xóa đề tự luyện'}
            description={
              type === 'exam'
                ? 'Xác nhận xóa đề thi. Hành động này không thể hoàn tác?'
                : 'Xác nhận xóa đề tự luyện. Hành động này không thể hoàn tác?'
            }
            onConfirm={() => handleDelete(record.id).then()}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleDelete = async (id: string) => {
    try {
      const hasDeleteConfirm = type === 'exam' ? await hasDeleteExamConfirm(id) : await hasDeletePracticeConfirm(id);
      if (hasDeleteConfirm) {
        setDeleteConfirm({
          open: true,
          examId: id,
        });
      } else {
        await processDelete(id);
      }
    } catch (err) {
      processError(err);
    }
  };

  const processDelete = async (id: string) => {
    if (loading) return;
    setLoading(true);
    try {
      type === 'exam' ? await deleteExam(id) : await deletePractice(id);
      message.success('Xoá thành công');
      await loadListExamOfSubject();
    } catch (err) {
      processError(err);
    } finally {
      setLoading(false);
      setDeleteConfirm({
        open: false,
      });
    }
  };

  const moveEditView = async (record: Exam) => {
    navigate(`/${type}-management/subject/${params.subjectId}/${type}s/${record.id}`);
  };

  const loadListExamOfSubject = useApiDelay(async function (isChangeSubject?: boolean) {
    setLoading(true);
    const parameters = {
      keyword,
      size: pageSize,
      page: currentPage,
      sortBy,
      direction,
      subjectId: params.subjectId,
      schoolYearId: schoolYearFilter,
      approvalStatus: approvalStatusFilter,
      fromCMS: true,
    };
    const response = type === 'exam' ? await pageExams(parameters) : await pagePractices(parameters);
    setExamSubjectPage(response);
    setData(response.content);
    setLoading(false);
    setTotalElements(response.totalElements);

    if (isChangeSubject) {
      setSchoolYearFilter('');
      setApprovalStatusFilter('');
      setKeyword('');
    }
  });

  const onChangeSelect = function (e: any, fieldName: string) {
    switch (fieldName) {
      case 'schoolYear':
        setSchoolYearFilter(e);
        break;
      case 'approvalStatus':
        setApprovalStatusFilter(e);
        break;
      default:
        break;
    }
  };

  const onUpdateApprovalStatusRecord = async (e: any, record: Exam) => {
    try {
      await updateApprovalStatus(record.id, e);
      const examSubjectPageTemp = { ...examSubjectPage } as any;
      examSubjectPageTemp.content.forEach((recordTemp: any) => {
        if (recordTemp.id === record.id) {
          recordTemp.approvalStatus = e;
        }
      });
      setExamSubjectPage(examSubjectPageTemp);
      setData(examSubjectPageTemp.content);
      message.success('Cập nhật trạng thái thành công.');
    } catch (err) {
      processError(err);
    }
  };

  const onClickItemDropDown = function (e: any) {
    if (e.key === 'generate-random-exams') {
      setIsOpen(true);
    }
  };

  const getRandomExam = async function (templateId: string) {
    dispatch(updateLoading(true));
    try {
      const examImportRequest = await getGenerateRandomExam(templateId);
      const examTemplate = await findExamTemplatesById(templateId);
      const subject =
        examImportRequest && examImportRequest.subject
          ? subjects.find((subject) => subject.id === examImportRequest.subject?.id)
          : null;
      if (examImportRequest) {
        dispatch(
          initExamEdit({
            examImportRequest,
            examTemplate,
            subject: subject || null,
            practice: type === 'practice',
          }),
        );
        navigate(`/exam-management/subject/${params.subjectId}/exams/preview-generate-exam`);
      } else message.error('Đã có lỗi xảy ra');
    } catch (error) {
      processError(error);
    }
    dispatch(updateLoading(false));
  };

  useEffect(() => {
    loadListExamOfSubject().then().catch(processError);
  }, [currentPage, pageSize, sortBy, direction, schoolYearFilter, keyword, approvalStatusFilter]);

  useEffect(() => {
    loadListExamOfSubject(true).then().catch(processError);
  }, [params.subjectId]);

  return (
    <div className="w-full h-full p-4" ref={rootElement}>
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          {type === 'exam' ? (
            <Dropdown.Button icon={<DownOutlined />} menu={{ items, onClick: onClickItemDropDown }} trigger={['click']}>
              <div className="w-full">
                <PlusOutlined className="text-[15px]" />
                <span className="pl-2">Thêm mới</span>
              </div>
            </Dropdown.Button>
          ) : null}
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <div>Năm</div>
            <Select
              onChange={(e) => onChangeSelect(e, 'schoolYear')}
              className="w-full"
              placeholder="Bộ lọc-Năm học"
              style={{ flex: 1 }}
              value={schoolYearFilter}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(
                schoolYearList.map((schoolYear) => ({
                  value: schoolYear.id,
                  label: schoolYear.code,
                })),
              )}
            />
          </div>
          <div className="min-w-[250px] max-w-[250px] flex items-center gap-2">
            <div>Trạng thái:</div>
            <Select
              onChange={(e) => onChangeSelect(e, 'approvalStatus')}
              className="w-full"
              placeholder="Trạng thái"
              style={{ flex: 1 }}
              value={approvalStatusFilter}
              options={[
                {
                  value: '',
                  label: 'Tất cả',
                },
              ].concat(approvalStatusList)}
            />
          </div>
        </div>
        <Input
          placeholder="Nhập từ khóa tìm kiếm"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="w-fit"
        />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          scroll={{ y: (window.innerHeight * 52) / 100 }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <SelectExamTemplate
        isOpen={isOpen}
        onSave={(templateId) => {
          getRandomExam(templateId);
        }}
        onCloseModal={() => {
          setIsOpen(false);
        }}
        subjectId={params.subjectId}
      />
      <ExamPreviewModal
        open={preview.open}
        exam={preview.exam}
        onCancel={() =>
          setPreview({
            open: false,
          })
        }
        zIndex={100}
      />
      <EditQuestionPopup onBeforeSuccess={loadListExamOfSubject} />
      <Modal
        title={<div className={'font-semibold text-primary text-xl'}>Xác nhận xóa</div>}
        footer={[]}
        open={deleteConfirm.open}
        onCancel={() => setDeleteConfirm({ open: false })}
        closable={false}
        width={460}
      >
        <p className={'text-primary text-md'}>
          Đề thi đã có ca làm hoặc được gắn vào sách. Xác nhận xóa toàn bộ thông tin liên quan đến đề thi này?
        </p>
        <div className={'flex justify-between gap-x-1 mt-4'}>
          <Button
            className={'h-[36px] rounded-3xl bg-white font-bold border-light-blue text-light-blue'}
            onClick={() => setDeleteConfirm({ open: false })}
          >
            Hủy
          </Button>
          <Button
            onClick={() => processDelete(deleteConfirm.examId as string)}
            className={'h-[36px] rounded-3xl font-bold bg-light-blue text-white'}
          >
            Xác nhận xóa
          </Button>
        </div>
      </Modal>
    </div>
  );
}
